/**
 * Service for managing lesson prompts in Supabase
 * Provides CRUD operations with version history support
 */

import { supabase } from '@/integrations/supabase/client';
import {
  LessonPromptRecord,
  LessonPromptData,
  LessonPromptInput,
  LessonPromptHistoryRecord,
  PromptHistoryEntry,
  LessonWithPromptStatus,
  transformPromptRecord,
  transformPromptInput,
  transformHistoryRecord,
} from '@/types/lessonPrompt';
import { microSessionCatalog } from '@/data/microSessions';

class LessonPromptService {
  /**
   * Get a single prompt by lesson ID
   * Returns null if no database record exists (fallback to file-based)
   */
  async getPromptByLessonId(lessonId: string): Promise<LessonPromptData | null> {
    const { data, error } = await supabase
      .from('lesson_prompts')
      .select('*')
      .eq('lesson_id', lessonId)
      .eq('is_active', true)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        // No rows returned - this is expected when no DB override exists
        return null;
      }
      console.error('[lessonPromptService] Error fetching prompt:', error);
      throw error;
    }

    return transformPromptRecord(data as LessonPromptRecord);
  }

  /**
   * Get all prompts from database
   * Admin only - includes inactive prompts
   */
  async getAllPrompts(): Promise<LessonPromptData[]> {
    const { data, error } = await supabase
      .from('lesson_prompts')
      .select('*')
      .order('updated_at', { ascending: false });

    if (error) {
      console.error('[lessonPromptService] Error fetching all prompts:', error);
      throw error;
    }

    return (data as LessonPromptRecord[]).map(transformPromptRecord);
  }

  /**
   * Get all lessons with their prompt status (has DB override or uses file default)
   */
  async getLessonsWithPromptStatus(): Promise<LessonWithPromptStatus[]> {
    // Get all DB prompts
    const { data: dbPrompts, error } = await supabase
      .from('lesson_prompts')
      .select('lesson_id, updated_at, updated_by, version');

    if (error) {
      console.error('[lessonPromptService] Error fetching prompt status:', error);
      throw error;
    }

    // Create a map of lesson_id -> db prompt info
    const dbPromptMap = new Map(
      (dbPrompts || []).map((p) => [p.lesson_id, p])
    );

    // Get profile names for updated_by users
    const userIds = [...new Set((dbPrompts || []).map((p) => p.updated_by).filter(Boolean))];
    const userNameMap = new Map<string, string>();

    if (userIds.length > 0) {
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, email')
        .in('id', userIds);

      profiles?.forEach((p) => {
        const name = p.first_name && p.last_name
          ? `${p.first_name} ${p.last_name}`
          : p.email || 'Unknown';
        userNameMap.set(p.id, name);
      });
    }

    // Combine with catalog
    return microSessionCatalog.map((session) => {
      const dbInfo = dbPromptMap.get(session.id);
      return {
        id: session.id,
        title: session.title,
        displayTitle: session.displayTitle,
        hasDbPrompt: !!dbInfo,
        promptSource: dbInfo ? 'database' : 'file',
        lastUpdated: dbInfo?.updated_at,
        updatedBy: dbInfo?.updated_by,
        updatedByName: dbInfo?.updated_by ? userNameMap.get(dbInfo.updated_by) : undefined,
        version: dbInfo?.version,
      };
    });
  }

  /**
   * Save or update a lesson prompt
   * Creates history record automatically via database trigger
   */
  async savePrompt(input: LessonPromptInput, userId: string): Promise<LessonPromptData> {
    const record = transformPromptInput(input);

    // Check if prompt already exists
    const { data: existing } = await supabase
      .from('lesson_prompts')
      .select('id')
      .eq('lesson_id', input.lessonId)
      .single();

    let result;

    if (existing) {
      // Update existing
      const { data, error } = await supabase
        .from('lesson_prompts')
        .update({
          ...record,
          updated_by: userId,
        })
        .eq('lesson_id', input.lessonId)
        .select()
        .single();

      if (error) {
        console.error('[lessonPromptService] Error updating prompt:', error);
        throw error;
      }
      result = data;
    } else {
      // Insert new
      const { data, error } = await supabase
        .from('lesson_prompts')
        .insert({
          ...record,
          created_by: userId,
          updated_by: userId,
        })
        .select()
        .single();

      if (error) {
        console.error('[lessonPromptService] Error inserting prompt:', error);
        throw error;
      }
      result = data;
    }

    return transformPromptRecord(result as LessonPromptRecord);
  }

  /**
   * Delete a prompt (reverts lesson to file-based default)
   */
  async deletePrompt(lessonId: string): Promise<void> {
    const { error } = await supabase
      .from('lesson_prompts')
      .delete()
      .eq('lesson_id', lessonId);

    if (error) {
      console.error('[lessonPromptService] Error deleting prompt:', error);
      throw error;
    }
  }

  /**
   * Soft delete - mark as inactive instead of deleting
   */
  async deactivatePrompt(lessonId: string, userId: string): Promise<void> {
    const { error } = await supabase
      .from('lesson_prompts')
      .update({
        is_active: false,
        updated_by: userId,
      })
      .eq('lesson_id', lessonId);

    if (error) {
      console.error('[lessonPromptService] Error deactivating prompt:', error);
      throw error;
    }
  }

  /**
   * Get version history for a lesson prompt
   */
  async getPromptHistory(lessonId: string): Promise<PromptHistoryEntry[]> {
    const { data, error } = await supabase
      .from('lesson_prompt_history')
      .select('*')
      .eq('lesson_id', lessonId)
      .order('changed_at', { ascending: false });

    if (error) {
      console.error('[lessonPromptService] Error fetching history:', error);
      throw error;
    }

    // Get profile names for changed_by users
    const userIds = [...new Set((data || []).map((h) => h.changed_by).filter(Boolean))];
    const userNameMap = new Map<string, string>();

    if (userIds.length > 0) {
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, email')
        .in('id', userIds);

      profiles?.forEach((p) => {
        const name = p.first_name && p.last_name
          ? `${p.first_name} ${p.last_name}`
          : p.email || 'Unknown';
        userNameMap.set(p.id, name);
      });
    }

    return (data as LessonPromptHistoryRecord[]).map((record) => ({
      ...transformHistoryRecord(record),
      changedByName: record.changed_by ? userNameMap.get(record.changed_by) : undefined,
    }));
  }

  /**
   * Restore a prompt from a specific history entry
   */
  async restoreFromHistory(historyId: string, userId: string): Promise<LessonPromptData> {
    // Get the history record
    const { data: historyRecord, error: historyError } = await supabase
      .from('lesson_prompt_history')
      .select('*')
      .eq('id', historyId)
      .single();

    if (historyError) {
      console.error('[lessonPromptService] Error fetching history record:', historyError);
      throw historyError;
    }

    const previousData = (historyRecord as LessonPromptHistoryRecord).previous_data;

    // Restore by updating the current prompt with the previous data
    const { data, error } = await supabase
      .from('lesson_prompts')
      .update({
        ai_prompt: previousData.ai_prompt,
        chat_intro: previousData.chat_intro,
        intro_prompt: previousData.intro_prompt,
        prompt_guidance: previousData.prompt_guidance,
        narrative_problem: previousData.narrative_problem,
        narrative_what: previousData.narrative_what,
        narrative_why: previousData.narrative_why,
        narrative_how: previousData.narrative_how,
        narrative_bridge: previousData.narrative_bridge,
        context_scenarios: previousData.context_scenarios,
        quick_prompts: previousData.quick_prompts,
        is_active: true,
        updated_by: userId,
      })
      .eq('lesson_id', previousData.lesson_id)
      .select()
      .single();

    if (error) {
      console.error('[lessonPromptService] Error restoring prompt:', error);
      throw error;
    }

    return transformPromptRecord(data as LessonPromptRecord);
  }

  /**
   * Bulk import prompts from file-based data
   * Used for initial migration
   */
  async bulkImport(prompts: LessonPromptInput[], userId: string): Promise<number> {
    let imported = 0;

    for (const prompt of prompts) {
      try {
        await this.savePrompt(prompt, userId);
        imported++;
      } catch (error) {
        console.error(`[lessonPromptService] Failed to import ${prompt.lessonId}:`, error);
      }
    }

    return imported;
  }
}

export const lessonPromptService = new LessonPromptService();
